import { useState, useMemo } from "react";
import { useSelector } from "react-redux";
import { DateTime } from "luxon";
import { RootState } from "typedefs";
import { EventModel } from "app/models/EventModel";
import { CalendarPreferences } from "app/models/CalendarPreferences";

const mediaWatcher = window.matchMedia("(max-width: 820px)");

interface UseCalendarStateParams {
  preferences: CalendarPreferences;
  facilityBookingEnabled: boolean;
  facilityLessonRates: any[];
  coachLessonRates: any[];
}

const useCalendarState = ({
  preferences,
  facilityBookingEnabled,
  facilityLessonRates,
  coachLessonRates,
}: UseCalendarStateParams) => {
  const [isSelecting, setIsSelecting] = useState(false);
  const [calendarInterval, setCalendarInterval] = useState<{
    start: DateTime;
    end: DateTime;
  }>({ start: null, end: null });
  const [currentDay, setCurrentDay] = useState<DateTime>(null);

  const isMobile = mediaWatcher.matches;
  const calendarView = preferences.calendarView;

  // Full zoned instants (not bare dates): the backend interprets date-only
  // params at UTC midnight, which truncates the first hours of the range for
  // UTC-positive timezones (CN-2586).
  const isoStartDate = useMemo(
    () => calendarInterval.start?.toISO(),
    [calendarInterval.start?.toMillis()],
  );
  const isoEndDate = useMemo(
    () => calendarInterval.end?.toISO(),
    [calendarInterval.end?.toMillis()],
  );

  const dateCalendar = currentDay || DateTime.now();

  const selectedEvent: EventModel = useSelector(
    (state: RootState) => state.events.selected,
  );
  const isEditingEvent: boolean = useSelector(
    (state: RootState) => state.events.editSelected,
  );
  const newEvent = useSelector((state: RootState) => state.events.newEvent);
  const showInfoPopup = useSelector(
    (state: RootState) => state.events.showInfoPopup,
  );
  const infoPopupContent = useSelector(
    (state: RootState) => state.events.infoPopupContent,
  );

  const pendingCreate: boolean = useSelector(
    (state: RootState) => state.events.pendingCreate,
  );
  const pendingEdit: boolean = useSelector(
    (state: RootState) => state.events.pendingEdit,
  );

  const eventForForm = selectedEvent || newEvent;
  const showEventForm = (selectedEvent && isEditingEvent) || newEvent;
  const eventSubmitErrors = useSelector(
    (state: RootState) => state.events.error,
  );

  // Use facility rates when FF enabled, otherwise use coach rates
  const lessonRates = facilityBookingEnabled
    ? facilityLessonRates
    : coachLessonRates;

  const defaultBookingOptionCoach = lessonRates?.find(
    (rate) => rate.defaultRate === true,
  );

  const defaultDurationInMinutesCoach = parseInt(
    defaultBookingOptionCoach?.duration || "30",
    10,
  );

  const handleMouseDown = () => setIsSelecting(true);
  const handleMouseUp = () => setIsSelecting(false);

  return {
    isSelecting,
    calendarInterval,
    setCalendarInterval,
    currentDay,
    setCurrentDay,
    isMobile,
    calendarView,
    isoStartDate,
    isoEndDate,
    dateCalendar,
    selectedEvent,
    isEditingEvent,
    newEvent,
    showInfoPopup,
    infoPopupContent,
    eventForForm,
    showEventForm,
    eventSubmitErrors,
    lessonRates,
    pendingCreate,
    pendingEdit,
    defaultDurationInMinutesCoach,
    handleMouseDown,
    handleMouseUp,
  };
};

export default useCalendarState;
